/**
 * The arithmetic behind the organization chart: who sits where, and where the
 * connector lines run. Kept out of the component so each rule can be pinned
 * without rendering.
 *
 * Nothing here knows a reporting line. The CRM records no reports-to field on
 * a broker, a firm or a deal contact, so the chart is honest about what it is:
 * the firm at the top and the people saved under it beneath, ordered by title
 * seniority. The tiers are rows of an even number of equal columns, with one
 * trunk down the gap at the centre; every row branches off that trunk, so the
 * connectors stay exact however many rows the people need.
 *
 * The one level the CRM does record between a firm and its people is where
 * each person sits (CY-24, 26 Oct): a firm whose people are in two or more
 * offices is drawn firm, office, people. Offices are only ever what a record
 * says, folded for case and spacing and nothing more.
 */

/** The horizontal gap between cards in a row, in px. Connector math depends on it. */
export const GAP_PX = 12
/** The narrowest a person card is allowed to get before a row loses a column. */
export const MIN_CARD_PX = 140
/** Used until the chart has been measured (and wherever nothing can be measured). */
export const DEFAULT_COLUMNS = 2
/** Most people in one row. Even, so the centre trunk falls in a gap, not on a card. */
export const MAX_COLUMNS = 4

/**
 * Columns for a chart `width` px wide. Always even (2 or 4): the trunk runs down
 * the gap between the two middle columns, which only exists with an even count.
 * An unmeasured chart (no layout, zero width) gets the default.
 */
export function columnsForWidth(width: number | null | undefined): number {
  if (!width || !Number.isFinite(width) || width <= 0) return DEFAULT_COLUMNS
  const fit = Math.floor((width + GAP_PX) / (MIN_CARD_PX + GAP_PX))
  return fit >= MAX_COLUMNS ? MAX_COLUMNS : DEFAULT_COLUMNS
}

/** Splits `items` into consecutive rows of at most `size`. */
export function chunkRows<T>(items: readonly T[], size: number): T[][] {
  const per = Math.max(1, Math.floor(size))
  const rows: T[][] = []
  for (let i = 0; i < items.length; i += per) rows.push(items.slice(i, i + per))
  return rows
}

/** Rank of a title not recognised as one of the five ladder titles: after all of them. */
export const UNRANKED = 100

/**
 * Seniority of a title on the ladder Managing Director, Director, Vice
 * President, Associate, Analyst (lower is more senior), case-insensitively.
 * Within a rung, "Executive" and "Senior" come first and "Assistant", "Junior"
 * or "Associate" (as in Associate Director) come last. A title that is not on
 * the ladder, or a missing one, is `UNRANKED`.
 */
export function seniorityRank(title: string | null | undefined): number {
  const t = (title ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim()
  if (!t) return UNRANKED
  let rung: number
  if (/\bmanaging director\b|\bmd\b/.test(t)) rung = 0
  else if (/\bdirector\b/.test(t)) rung = 1
  else if (/\b(vice president|vp|svp|evp|avp)\b/.test(t)) rung = 2
  else if (/\bassociate\b/.test(t)) rung = 3
  else if (/\banalyst\b/.test(t)) rung = 4
  else return UNRANKED
  let modifier = 2
  if (/\b(executive|exec|evp)\b/.test(t)) modifier = 0
  else if (/\b(senior|sr|svp)\b/.test(t)) modifier = 1
  else if (/\b(assistant|asst|avp|junior|jr)\b/.test(t) || (rung < 3 && /\bassociate\b/.test(t))) modifier = 3
  return rung * 10 + modifier
}

/**
 * What kind of firm the CRM says this is. A brokerage reads "Brokerage"; any
 * other recorded type is shown as recorded; with none (or records that
 * disagree) it is just an organization. Never guessed from the name.
 */
export function firmKindLabel(records: readonly { companyType: string | null }[]): string {
  const kinds = new Map<string, string>()
  for (const record of records) {
    const kind = (record.companyType ?? '').trim()
    if (kind && !kinds.has(kind.toLowerCase())) kinds.set(kind.toLowerCase(), kind)
  }
  if (kinds.size !== 1) return 'Organization'
  const [[key, kind]] = [...kinds]
  return key === 'broker' ? 'Brokerage' : kind
}

interface Orderable {
  id: string
  name: string | null
  title: string | null
  email: string | null
}

/** The name a person is shown under: their name, else their address, else their id. */
export function displayName(person: Pick<Orderable, 'id' | 'name' | 'email'>): string {
  return person.name || person.email || person.id
}

/**
 * People most senior first. Equal rank (and every unranked title) falls back
 * to alphabetical by name, then id, so the order is the same on every render.
 */
export function orderBySeniority<T extends Orderable>(people: readonly T[]): T[] {
  return people
    .map((person) => ({ person, rank: seniorityRank(person.title), label: displayName(person) }))
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }) ||
        a.person.id.localeCompare(b.person.id),
    )
    .map((entry) => entry.person)
}

/** The office node the people with no office on record sit under. */
export const OFFICE_NOT_RECORDED = 'Office not recorded'

/**
 * The key two recorded offices are one office by: case and spacing folded,
 * nothing else. "Chicago, IL" and "chicago ,IL" are one office; "Chicago, IL"
 * and "Chicago, Illinois" stay two, because calling them one would be a
 * guess. A blank office is no office.
 */
export function officeKey(office: string | null | undefined): string | null {
  const key = (office ?? '')
    .toLowerCase()
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ')
    .trim()
  return key || null
}

export interface OfficeGroup<T> {
  /** `officeKey` of the office; null for the people with no office on record. */
  key: string | null
  /** The office as recorded (its commonest spelling), or `OFFICE_NOT_RECORDED`. */
  label: string
  /** Most senior first. */
  people: T[]
}

/**
 * The commonest of the spellings counted. A tie goes to the alphabetically
 * first, and between two that differ only in case, to the one with capitals
 * ("New York, NY" over "new york, NY"), by plain character order.
 */
function commonest(spellings: Map<string, number>): string {
  return [...spellings].sort(
    ([a, n], [b, m]) =>
      m - n || a.localeCompare(b, undefined, { sensitivity: 'base' }) || (a < b ? -1 : a > b ? 1 : 0),
  )[0][0]
}

/**
 * The firm's people under their offices, or null where the chart stays one
 * tier: with fewer than two offices on record an office node would only
 * restate the firm, or invent a place. Offices with the most people come
 * first, then alphabetically; the people with no office on record come last,
 * under `OFFICE_NOT_RECORDED`. Each office lists its people most senior first.
 */
export function groupByOffice<T extends Orderable & { office?: string | null }>(
  people: readonly T[],
): OfficeGroup<T>[] | null {
  const offices = new Map<string, { spellings: Map<string, number>; people: T[] }>()
  const unrecorded: T[] = []
  for (const person of people) {
    const key = officeKey(person.office)
    if (key === null) {
      unrecorded.push(person)
      continue
    }
    const office = offices.get(key) ?? { spellings: new Map<string, number>(), people: [] }
    const spelling = (person.office ?? '').replace(/\s+/g, ' ').trim()
    office.spellings.set(spelling, (office.spellings.get(spelling) ?? 0) + 1)
    office.people.push(person)
    offices.set(key, office)
  }
  if (offices.size < 2) return null
  const groups: OfficeGroup<T>[] = [...offices]
    .map(([key, office]) => ({ key, label: commonest(office.spellings), people: orderBySeniority(office.people) }))
    .sort(
      (a, b) =>
        b.people.length - a.people.length ||
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }) ||
        (a.key as string).localeCompare(b.key as string),
    )
  if (unrecorded.length) groups.push({ key: null, label: OFFICE_NOT_RECORDED, people: orderBySeniority(unrecorded) })
  return groups
}

/**
 * X position, as a CSS length, of the centre of column `index` (0-based) in a
 * grid of `columns` equal columns separated by `GAP_PX`. The grid's width is
 * the containing block's `100%`.
 */
function columnCentre(index: number, columns: number): string {
  const share = (index + 0.5) / columns
  return `((100% + ${GAP_PX}px) * ${share} - ${GAP_PX / 2}px)`
}

export interface TierGeometry {
  /** `left` of the horizontal bus: the centre of the first card. */
  busLeft: string
  /** `right` of the horizontal bus: ends at the farther of the last card and the trunk. */
  busRight: string
  /** Whether the bus is drawn at all (a lone, centred card hangs straight off the trunk). */
  hasBus: boolean
}

/**
 * Where one row's bus runs. `count` cards sit in the first `count` of
 * `columns` columns; the trunk is at 50%. The bus reaches from the first card's
 * centre to whichever is farther right, the last card's centre or the trunk,
 * so a short last row still joins the trunk. A single column has no bus.
 */
export function tierGeometry(count: number, columns: number): TierGeometry {
  if (columns <= 1) return { busLeft: '50%', busRight: '50%', hasBus: false }
  const last = columnCentre(Math.max(count, 1) - 1, columns)
  const end = count > columns / 2 ? last : '50%'
  return {
    busLeft: `calc${columnCentre(0, columns)}`,
    // +1px so the bus covers the 1px-wide drop (or trunk) it ends on.
    busRight: `calc(100% - (${end} + 1px))`,
    hasBus: true,
  }
}
